// LinhaPessoa (DESIGN §5.29): uma linha só para todo lugar onde o aluno ESCOLHE ou RECONHECE um colega — Marcar
// pessoas, Treinar com alguém, Treinando agora, Convites e Pessoas bloqueadas
// (docs/prototipo/comunidade-fotos-marcacao.html §4 a §8; mudança do founder de 02/10/2026: nome E sobrenome).
//
// min-h 64, avatar 40, gap 12; nome `text-titulo`; contexto `text-rotulo texto-3`, no máximo 2 itens com " · ",
// ligação em `texto-2`, "Treinando agora" em `ao-vivo` com ponto de 8. Prioridade em telas estreitas (360, ou 390 com
// "Convidar"/"Desbloquear" ao lado), QA final D5:
// - nome e sobrenome nunca somem: o nome quebra entre as palavras em até 2 linhas e só então trunca. É o que
//   diferencia homônimos (decisão do founder de 02/10).
// - a ligação nunca é cortada: sem espaço ao lado do 1º item, ela desce inteira para a 2ª linha do contexto, sem o
//   "·" no começo da linha.
// - o turno é o que sai primeiro: com menos de 48 px, some junto com o separador (quebra para uma linha escondida).
// Variantes: "escolha" (a linha inteira é o botão), "com-acao" (ação à direita: "Convidar"/"Desbloquear") e
// "informativa" (convites).
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import type { ItemContexto } from "../lib/pessoa";
import { Avatar } from "./Avatar";

export interface PropsLinhaPessoa {
  /** Nome já montado por `nomeNaLista` (ou o título, como "Camila Rodrigues te chamou"). */
  nome: string;
  /** Nome para as iniciais do avatar (o nome da lista: "Camila Rodrigues" -> "CR"). */
  nomeAvatar?: string;
  fotoUrl?: string | null;
  itens: readonly ItemContexto[];
  variante: "escolha" | "com-acao" | "informativa";
  /** Slot da direita: rótulo "Marcado", botão "Convidar", status do convite. */
  direita?: ReactNode;
  desativada?: boolean;
  aoEscolher?: () => void;
  /** aria-label da linha de escolha ("Marcar Camila Rodrigues. Treinou com você em 28/09, treina à noite"). */
  rotulo?: string;
  /** Anel verde de quem está treinando agora. */
  anelVivo?: boolean;
  className?: string;
}

/** Separador com o espaço dos dois lados (6 + ponto + 6): é recortado quando o item abre a linha. */
function Separador() {
  return (
    <span aria-hidden className="w-4 shrink-0 text-center">
      ·
    </span>
  );
}

function Contexto({ itens }: { itens: readonly ItemContexto[] }) {
  if (itens.length === 0) return null;
  const ultimo = itens.length - 1;
  const [primeiro, segundo] = itens;
  if (primeiro && segundo?.tipo === "relacao") {
    // Ligação em 2º: cada item traz o separador à esquerda numa faixa de 16 px; a margem negativa põe essa faixa do
    // 1º item (e a da ligação, quando ela desce de linha) fora do recorte. A ligação não encolhe: cabe ao lado ou desce.
    const tom = (it: ItemContexto) =>
      cn(
        "min-w-0 truncate",
        it.tom === "relacao" && "text-texto-2",
        it.tom === "vivo" &&
          "inline-flex items-center gap-1.5 text-ao-vivo before:size-2 before:shrink-0 before:rounded-full before:bg-ao-vivo before:content-['']",
      );
    return (
      <span className="min-w-0 overflow-hidden text-rotulo text-texto-3">
        <span className="-ml-4 flex flex-wrap items-center">
          <span className={cn("flex min-w-0 items-center", primeiro.tipo === "extra" ? "shrink" : "flex-none")}>
            <Separador />
            <span className={cn(tom(primeiro), "whitespace-nowrap")}>{primeiro.texto}</span>
          </span>
          <span className="flex max-w-full min-w-0 flex-none items-center">
            <Separador />
            <span className={cn(tom(segundo), "whitespace-nowrap")}>{segundo.texto}</span>
          </span>
        </span>
      </span>
    );
  }
  return (
    <span className="flex max-h-[1.35em] min-w-0 flex-wrap items-center gap-x-1.5 overflow-hidden text-rotulo text-texto-3">
      {itens.map((it, i) => {
        const texto = (
          <span
            className={cn(
              "whitespace-nowrap",
              it.tom === "relacao" && "text-texto-2",
              it.tom === "vivo" &&
                "inline-flex items-center gap-1.5 text-ao-vivo before:size-2 before:shrink-0 before:rounded-full before:bg-ao-vivo before:content-['']",
              (i === ultimo || it.tipo === "extra") && "min-w-0 truncate",
            )}
          >
            {it.texto}
          </span>
        );
        if (i === 0)
          return (
            <span
              key={i}
              className={cn(ultimo === 0 || it.tipo === "extra" ? "flex min-w-0 shrink" : "flex flex-none")}
            >
              {texto}
            </span>
          );
        // 2º item: separador junto. Turno com base de 48 px: sem espaço, quebra (e some); com espaço, trunca. A ligação
        // nunca some: base 0, ocupa o que sobra e trunca.
        return (
          <span
            key={i}
            className={cn(
              "flex min-w-0 gap-1.5",
              it.tipo === "turno" ? "max-w-max grow basis-12" : "min-w-16 flex-1 basis-0",
            )}
          >
            <span aria-hidden>·</span>
            {texto}
          </span>
        );
      })}
    </span>
  );
}

export function LinhaPessoa({
  nome,
  nomeAvatar,
  fotoUrl,
  itens,
  variante,
  direita,
  desativada,
  aoEscolher,
  rotulo,
  anelVivo,
  className,
}: PropsLinhaPessoa) {
  const avatar = (
    <span
      className="shrink-0 rounded-full"
      style={anelVivo ? { boxShadow: "0 0 0 2px var(--gs-painel), 0 0 0 4px var(--gs-ao-vivo)" } : undefined}
    >
      <Avatar nome={nomeAvatar ?? nome} fotoUrl={fotoUrl} tamanho={40} />
    </span>
  );
  const corpo = (
    <>
      {avatar}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="line-clamp-2 text-titulo break-words text-tinta">{nome}</span>
        <Contexto itens={itens} />
      </span>
      {direita}
    </>
  );
  const base = cn(
    "flex min-h-16 w-full items-center gap-3 py-2 text-left",
    desativada && "opacity-50",
    className,
  );
  if (variante === "escolha") {
    return (
      <button type="button" className={base} disabled={desativada} aria-label={rotulo} onClick={aoEscolher}>
        {corpo}
      </button>
    );
  }
  return <div className={base}>{corpo}</div>;
}

/** Esqueleto da LinhaPessoa (DESIGN §5.14): avatar 40 e duas barras, mesma altura da linha real. */
export function EsqueletoLinhaPessoa({ larguras = [42, 64] }: { larguras?: [number, number] }) {
  return (
    <div aria-hidden className="flex min-h-16 items-center gap-3 py-2">
      <span className="esqueleto block size-10 shrink-0 rounded-full" />
      <span className="flex flex-1 flex-col gap-2">
        <span className="esqueleto block h-4 rounded-celula" style={{ width: `${larguras[0]}%` }} />
        <span className="esqueleto block h-3 rounded-celula" style={{ width: `${larguras[1]}%` }} />
      </span>
    </div>
  );
}
